import React, { useEffect, useState } from 'react'

import { ProductV2, gameIdSchema, getLocale } from '@common'
import { useTranslations } from 'next-intl'
import Head from 'next/head'
import { useRouter } from 'next/router'

import { api } from '@/api'
import { Loader } from '@/components/Loader'
import { PageMetaTags } from '@/components/MetaTags'
import { Page } from '@/components/Page'
import { useProducts } from '@/features/common/useProducts'
import { Product } from '@/features/products/Product'
import { useTranslatable } from '@/localization/useTranslatable'
import { cartClient } from '@/services/cartClient'
import { setPublicPageCacheControl, wrapGetServerSideProps } from '@/ssr/wrapper'
import { findProductBySlugOrId } from '@/utils/products'
import { COLOR_BLUE } from '@/utils/styles'
import { useAuthenticatedOnly } from '@/utils/useAuthenticatedOnly'

import { setCurrentGame } from '../../features/common/commonSlice'
import { useGames } from '../../features/common/useGames'
import { LOGIN_STATUS } from '../../features/user/userSlice'
import { loadProductCopy } from '../../localization/MessageContext'
import { shortSlugToGameNameKey } from '../../localization/utils'
import { getAccountId } from '../../utils/getAccountId'
import { useAppDispatch } from '../../utils/useAppDispatch'
import { useAppSelector } from '../../utils/useAppSelector'

import styles from './product.module.scss'

const useProduct = (slugOrId: string, initialProduct: ProductV2 | null) => {
  const router = useRouter()
  const [loggedInAt, setLoggedInAt] = useState(Number.MAX_SAFE_INTEGER)
  const isLoggedIn = useAppSelector((state) => state.user.loginStatus === LOGIN_STATUS.LOGGED_IN)
  const { products, fulfilledTimeStamp = 0, refetch, isLoading } = useProducts()

  useEffect(() => {
    if (isLoggedIn) {
      setLoggedInAt(Date.now())
      refetch()
    } else {
      setLoggedInAt(Number.MAX_SAFE_INTEGER)
    }
  }, [isLoggedIn, refetch])

  if (initialProduct) {
    return { product: initialProduct, isLoading: false }
  }

  const product = products?.find(findProductBySlugOrId(slugOrId))
  if (isLoggedIn && loggedInAt <= fulfilledTimeStamp && !product) {
    router.push('/404')
  }
  return { product, isLoading }
}

export const ProductPage: React.FC<{
  slugOrId: string
  initialProduct: ProductV2 | null
}> = ({ slugOrId, initialProduct }) => {
  const dispatch = useAppDispatch()
  const t = useTranslations()
  const translatable = useTranslatable()
  const games = useGames()
  const { product, isLoading } = useProduct(slugOrId, initialProduct)

  useEffect(() => {
    if (product?.game) {
      dispatch(setCurrentGame(product.game))
    }
  }, [product, dispatch])

  useAuthenticatedOnly({
    isLoading,
    login: !Boolean(product),
    skip: Boolean(product),
  })
  const game = games.find((game) => game.slug === product?.game)
  const productTitle = product ? translatable(product.title) : ''
  const title = `${t('route_product', {
    product: productTitle ?? '',
    game: game ? t(shortSlugToGameNameKey(game.shortSlug)) : '',
  })}`

  return (
    <Page className={styles.Page}>
      {product && (
        <PageMetaTags
          title={translatable(product.title)}
          description={t('gen_prod_meta_description', { product: translatable(product.title) })}
        />
      )}
      <Head>
        <title>{title}</title>
      </Head>
      {product ? (
        <div className={styles.ProductContainer}>
          <Product product={product} isProductPage />
        </div>
      ) : (
        <Loader strokeColor={COLOR_BLUE} />
      )}
    </Page>
  )
}

export default ProductPage

const noSuchProductError = {
  notFound: true,
} as const

export const getServerSideProps = wrapGetServerSideProps(async (store, context) => {
  const slug = context.params?.slug

  if (!slug) {
    return noSuchProductError
  }

  const slugOrId = Array.isArray(slug) && slug[slug.length - 1]

  if (!slugOrId) {
    return noSuchProductError
  }

  const gameIdParseResult = gameIdSchema.safeParse(slug[0]?.split('-')[0])

  if (!gameIdParseResult.success) {
    return noSuchProductError
  }

  const gameId = gameIdParseResult.data
  const accountId = getAccountId(context)

  setPublicPageCacheControl(context, 30)
  const locale = getLocale(context)
  const viewerCountry = store.getState().config.viewerCountry
  const [{ data: products }, cart] = await Promise.all([
    store.dispatch(api.endpoints.fetchProducts.initiate({ locale, viewerCountry, gameId })),
    cartClient.getCart(accountId, gameId),
  ])
  const product = products?.find(findProductBySlugOrId(slugOrId)) ?? null

  await Promise.all(store.dispatch(api.util.getRunningQueriesThunk()))

  const productCopy = product ? await loadProductCopy(product, context.locale || 'en') : null
  const pageMessages =
    product && productCopy
      ? {
          [product.slug]: productCopy,
        }
      : {}
  const { api: apiState, config } = store.getState()

  return {
    props: {
      cart,
      products,
      gameId,
      slugOrId,
      initialProduct: product,
      initialState: { api: apiState, config },
    },
    messages: pageMessages,
  }
}, 'productSSR')
